import { useContext, useEffect, useMemo, useState } from 'react'
import { AudioInputSelection, AudioInputSettings, AudioTrackWithInfo } from '@/UserContext'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import { VolumeMeter } from '@/components/VolumeMeter'
import { getAudioEngineInstance } from '@/core/AudioEngine'
import { toFixed1WithoutTrailing0 } from '@/utils'
import { env } from '@/services/EnvironmentVariables'
import { useTranslation } from 'react-i18next'
import { RoomContext, RoomContextType } from '@/RoomContext'
import { encodeExtendedDeviceId } from '@/Logic'
import { Button } from '@/UIComponents2/Button'
import { ChannelSelect } from '@/components/AudioInputSelectionPanel/ChannelSelect'
import Tooltip from '@/UIComponents2/Tooltip'
import PlusIcon from '@/CustomIcons/PlusIcon'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { Checkbox } from '@/UIComponents2/Checkbox'
import InternalRedCrossIcon from './icons/InternalRedCrossIcon'
import InternalGreenCheckIcon from './icons/InternalGreenCheckIcon'
import InternalInfoIcon from './icons/InternalInfoIcon'
import * as Constants from '@/services/Constants'
import { cn } from '@/utlis/cn'

function findClosestNumber(v: number, list: number[]): number {
    let closest = list[0]
    let smallestDifference = Math.abs(v - closest)

    for (let i = 1; i < list.length; ++i) {
        const currentDifference = Math.abs(v - list[i])
        if (currentDifference < smallestDifference) {
            smallestDifference = currentDifference
            closest = list[i]
        }
    }

    return closest
}

export function ChannelSection({
    mediaSelection,
    currSettings,
    atrack,
}: {
    mediaSelection: AudioInputSelection
    currSettings: AudioInputSettings
    atrack: AudioTrackWithInfo | null
}) {
    const { t } = useTranslation()
    const { state: roomState } = useContext(RoomContext) as RoomContextType

    const {
        updateSelectedAudioDeviceAddChannelGroup,
        updateSelectedAudioDeviceReplaceChannelGroup,
        updateSelectedAudioDeviceRemoveChannelGroup,
    } = useMediaSelectionActions()

    const [showNewChannelGroupRow, setShowNewChannelGroupRow] = useState(false)

    const onAddChannel = () => {
        setShowNewChannelGroupRow(true)
    }

    const allowMultipleGroups = mediaSelection.settings.isAudioEngine
    const maxChannel = mediaSelection.settings.isVoiceQuality ? 1 : 255
    const allChannelsShared =
        currSettings.channelSelection.reduce((a, b) => a + b.length, 0) >= mediaSelection.source.maxInputChannels
    const canAddCannelGroup = allowMultipleGroups && !showNewChannelGroupRow && !allChannelsShared

    const newChannelGroupTooltip = useMemo(() => {
        if (!allowMultipleGroups) {
            return 'Change to pro audio quality to share more channels'
        }
        if (showNewChannelGroupRow) {
            return 'Adding channel group'
        }
        if (allChannelsShared) {
            return 'All channels are already shared'
        }

        return 'Share more channels'
    }, [allowMultipleGroups, showNewChannelGroupRow, allChannelsShared])

    return (
        <div className="flex flex-col gap-2">
            <div className="">{t('Channels')}</div>
            <div className="flex gap-2 flex-wrap">
                {/* channel list */}
                {currSettings.channelSelection.map((chgrp, i) => {
                    return (
                        <div className="flex items-center" key={chgrp.join('-')}>
                            <div className="pr-2">
                                {atrack && (
                                    <VolumeMeter
                                        stream={atrack}
                                        chgrp={chgrp}
                                        isMuted={
                                            roomState.myAudioMuteState[
                                                encodeExtendedDeviceId(atrack.info.deviceId, chgrp)
                                            ]
                                        }
                                        variant="pills"
                                    />
                                )}
                            </div>

                            <ChannelSelect
                                numChannels={mediaSelection.source.maxInputChannels}
                                maxChannel={maxChannel}
                                mergedChannels={mediaSelection.settings.isVoiceQuality}
                                value={chgrp}
                                sharedGroups={mediaSelection.settings.channelSelection}
                                onClose={() => setTimeout(() => setShowNewChannelGroupRow(false))}
                                onChange={(v) => {
                                    if (!v || v.value.length === 0) {
                                        return
                                    }
                                    // NOTE: update
                                    updateSelectedAudioDeviceReplaceChannelGroup(mediaSelection, chgrp, v.value)
                                }}
                                onRemove={() => updateSelectedAudioDeviceRemoveChannelGroup(mediaSelection, chgrp)}
                                triggerClassName="min-w-[70px]"
                            />
                        </div>
                    )
                })}

                {showNewChannelGroupRow && (
                    <ChannelSelect
                        defaultOpen={true}
                        numChannels={mediaSelection.source.maxInputChannels}
                        maxChannel={maxChannel}
                        mergedChannels={mediaSelection.settings.isVoiceQuality}
                        value={[]}
                        sharedGroups={mediaSelection.settings.channelSelection}
                        onClose={() => setTimeout(() => setShowNewChannelGroupRow(false))}
                        onChange={(v) => {
                            if (!v || v.value.length === 0) {
                                return
                            }
                            updateSelectedAudioDeviceAddChannelGroup(mediaSelection, v.value)
                        }}
                        onRemove={() => {}}
                        triggerClassName="min-w-[70px]"
                    />
                )}

                <div className="flex items-center">
                    <Tooltip text={newChannelGroupTooltip}>
                        <Button
                            disabled={!canAddCannelGroup}
                            className={cn('opacity-75', !canAddCannelGroup && 'cursor-not-allowed')}
                            onClick={onAddChannel}
                        >
                            <PlusIcon />
                        </Button>
                    </Tooltip>
                </div>
            </div>
        </div>
    )
}

export function QualitySelectionDescriptionSection({ mediaSelection }: { mediaSelection: AudioInputSelection }) {
    const { t } = useTranslation()

    const code = Constants.settingsToAudioQualityPesetCode(mediaSelection.settings)
    const preset = Constants.getAudioQualityPresets().find((c) => c.code === code)

    if (!preset) {
        return <></>
    }

    const avSyncExplanation = Constants.supportedFeaturesExplanation('avSync', preset.supportedFeatures.avSync)
    const echoCancellationExplanation = Constants.supportedFeaturesExplanation(
        'echoCancellation',
        preset.supportedFeatures.echoCancellation
    )
    const variableQualityExplanation = Constants.supportedFeaturesExplanation(
        'variableQuality',
        preset.supportedFeatures.variableQuality
    )

    return (
        <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="flex items-center gap-1">
                <Tooltip text={avSyncExplanation}>
                    <div>{preset.supportedFeatures.avSync ? <InternalGreenCheckIcon /> : <InternalRedCrossIcon />}</div>
                </Tooltip>
                <div>{t('Audio/video sync')}</div>
            </div>

            <div className="flex items-center gap-1 justify-center">
                <Tooltip text={echoCancellationExplanation}>
                    <div>
                        {preset.supportedFeatures.echoCancellation ? (
                            <InternalGreenCheckIcon />
                        ) : (
                            <InternalRedCrossIcon />
                        )}
                    </div>
                </Tooltip>
                <div>{t('Echo cancellation')}</div>
            </div>

            <div className="flex items-center gap-1 justify-end">
                <Tooltip text={variableQualityExplanation}>
                    <div>
                        {preset.supportedFeatures.variableQuality ? (
                            <InternalGreenCheckIcon />
                        ) : (
                            <InternalRedCrossIcon />
                        )}
                    </div>
                </Tooltip>
                <div>{t('Variable quality')}</div>
            </div>
        </div>
    )
}

export function LatencySelectionSection({
    mediaSelection,
    currSettings,
    setSettingsHelper,
}: {
    mediaSelection: AudioInputSelection
    currSettings: AudioInputSettings
    setSettingsHelper: (param: keyof AudioInputSettings, value: string) => void
}) {
    const { t } = useTranslation()

    const currReceiverBufferSizeMsValue = findClosestNumber(
        currSettings.receiverBufferSizeMs,
        Constants.availableBufferSizeMs
    )

    const options = Constants.availableBufferSizeMs.map((n) => {
        if (n >= 1000) {
            return { value: n + '', label: toFixed1WithoutTrailing0(n / 1000) + ' s' }
        } else {
            return { value: n + '', label: n + ' ms' }
        }
    })

    const fieldLabel = (
        <div className="flex items-center gap-2">
            <div>{t('Latency / buffer')}</div>
            <Tooltip
                text={t(
                    'Sets the audio buffer size. A larger buffer means more stable audio but with more delay. A smaller buffer means faster audio but more potential dropouts.'
                )}
            >
                <div className="pt-[1px]">
                    <InternalInfoIcon />
                </div>
            </Tooltip>
        </div>
    )

    return (
        <div>
            {mediaSelection.settings.isAudioEngine ? (
                <SimpleSelect
                    label={fieldLabel}
                    items={options}
                    value={currReceiverBufferSizeMsValue ? currReceiverBufferSizeMsValue + '' : '_none'}
                    onChange={(value) => setSettingsHelper('receiverBufferSizeMs', value)}
                    widthMatching
                />
            ) : (
                <SimpleSelect
                    label={fieldLabel}
                    items={[{ value: '_none', label: `(${t('Dynamic')})` }]}
                    value={'_none'}
                    disabled
                    onChange={(value) => {}}
                    widthMatching
                />
            )}
        </div>
    )
}

export function SampleRateSelectionSection({
    mediaSelection,
    atrack,
    currSettings,
    setSettingsHelper,
}: {
    mediaSelection: AudioInputSelection
    atrack: AudioTrackWithInfo | null
    currSettings: AudioInputSettings
    setSettingsHelper: (param: keyof AudioInputSettings, value: string) => void
}) {
    const [supportedSampleRates, set_supportedSampleRates] = useState<number[]>(Constants.commonSampleRates)

    const [rtcSampleRate, set_rtcSampleRate] = useState<number | null>(null)

    const { t } = useTranslation()

    const mediaSource = mediaSelection.source

    const value = currSettings?.sampleRate || '_none'

    useEffect(() => {
        if (atrack) {
            const sampleRate = atrack.getSettings()?.sampleRate || null
            if (sampleRate) {
                set_rtcSampleRate(sampleRate)
            }
        }
    }, [atrack])

    // check supported sample rates
    useEffect(() => {
        ;(async () => {
            if (!mediaSource.audioEngineDeviceId) {
                return
            }
            // on osx, checking the sample rate of a device (calling
            // the Pa_IsFormatSupported() in the audio engine) somehow
            // causes it to break the stream.
            if (env.OS === 'darwin') {
                return
            }

            const supported: number[] = await getAudioEngineInstance().checkSupportedSampleRates({
                audioEngineDeviceId: mediaSource.audioEngineDeviceId,
                deviceDirection: 'input',
                sampleRates: Constants.commonSampleRates,
            })
            if (supported.length > 0) {
                set_supportedSampleRates(supported)
            }
        })()
    }, [])

    const options = supportedSampleRates.map((value) => {
        const isDefault = mediaSource.defaultSampleRate === value
        const label = value.toLocaleString() + ' Hz' + (isDefault ? ' (default)' : '')
        return { value: value + '', label }
    })

    const rtcSampleRateStr = (typeof rtcSampleRate === 'number' ? rtcSampleRate.toLocaleString() : '-') + ' Hz'

    const fieldLabel = (
        <div className="flex items-center gap-2">
            <div>{t('Sample rate')}</div>

            <Tooltip text={t('Sets the sample rate. Only the supported sample rates are shown.')}>
                <div className="pt-[1px]">
                    <InternalInfoIcon />
                </div>
            </Tooltip>
        </div>
    )

    return (
        <div>
            {mediaSelection.settings.isAudioEngine ? (
                <SimpleSelect
                    label={fieldLabel}
                    items={options}
                    value={value + ''}
                    onChange={(value) => setSettingsHelper('sampleRate', value)}
                    widthMatching
                />
            ) : (
                <SimpleSelect
                    label={fieldLabel}
                    items={[{ value: '_', label: rtcSampleRateStr }]}
                    value={'_'}
                    widthMatching
                />
            )}
        </div>
    )
}

export function LoopbackToggleSection({ mediaSelection }: { mediaSelection: AudioInputSelection }) {
    const { t } = useTranslation()

    const { updateSelectedAudioDeviceProperties } = useMediaSelectionActions()

    const onLoopbackToggle = () => {
        const settingsNext = {
            ...mediaSelection.settings,
            enableLocalLoopback: !mediaSelection.settings.enableLocalLoopback,
        }
        updateSelectedAudioDeviceProperties(mediaSelection.source.deviceId, settingsNext)
    }

    return (
        <div className="flex items-center">
            {/* <div className="flex items-center w-36">
                <div className="mr-1">{t('Loopback')}:</div>

                <ModalCompatTooltip
                    content={t(
                        'Route system audio to your speakers. Without loopback, the audio will ' +
                            'only be sent to your collaborators.'
                    )}
                >
                    <MIconWrap size="small">
                        <HelpOutlineIcon />
                    </MIconWrap>
                </ModalCompatTooltip>
            </div> */}

            <Checkbox
                checked={mediaSelection.settings.enableLocalLoopback}
                label={t('Enable Loopback') || ''}
                onChange={onLoopbackToggle}
            />
        </div>
    )
}

export function MaxDriftSelectionSection({
    currSettings,
    setSettingsHelper,
}: {
    currSettings: AudioInputSettings
    setSettingsHelper: (param: keyof AudioInputSettings, value: string) => void
}) {
    const values = [10, 15, 20, 25, 30, 35, 40, 50, 75, 100, 250, 500, 750, 1000, 1500, 2000, 2500]

    const currMaxDriftValue = (() => {
        if (currSettings.receiverBufferMaxDriftMs === 0 || currSettings.receiverBufferMaxDriftMs == null) {
            return 0 // the default
        }
        return findClosestNumber(currSettings.receiverBufferMaxDriftMs, values)
    })()

    const options = (() => {
        const list = values.map((n) => {
            return { value: n + '', label: n + ' ms' }
        })
        list.unshift({ value: '0', label: 'Default' })
        return list
    })()

    const fieldLabel = (
        <div className="flex gap-1">
            <div>Buffer max drift</div>
            <Tooltip text={'ADVANCED: How much the buffer can drift from the set buffer size.'}>
                <div className="pt-[1px]">
                    <InternalInfoIcon />
                </div>
            </Tooltip>
        </div>
    )

    return (
        <div>
            <SimpleSelect
                label={fieldLabel}
                items={options}
                value={currMaxDriftValue + ''}
                onChange={(values) => setSettingsHelper('receiverBufferMaxDriftMs', values[0])}
            />
        </div>
    )
}

export function PacketSizeSelectionSection({
    currSettings,
    setSettingsHelper,
}: {
    currSettings: AudioInputSettings
    setSettingsHelper: (param: keyof AudioInputSettings, value: string) => void
}) {
    const packetSizes = [5, 10, 20, 40]

    const currReceiverBufferSizeMsValue = (() => {
        if (currSettings.networkPacketDurationMs === 0 || currSettings.networkPacketDurationMs == null) {
            return 0 // the default
        }
        return findClosestNumber(currSettings.networkPacketDurationMs, packetSizes)
    })()

    const options = (() => {
        const list = packetSizes.map((n) => {
            return { value: n + '', label: n + ' ms' }
        })
        list.unshift({ value: '0', label: 'Default' })
        return list
    })()

    const fieldLabel = (
        <div className="flex items-center gap-1">
            <div>Packet duration:</div>
            <Tooltip text={'ADVANCED: How much audio a single packet holds.'}>
                <div className="pt-[1px]">
                    <InternalInfoIcon />
                </div>
            </Tooltip>
        </div>
    )

    return (
        <div>
            <SimpleSelect
                label={fieldLabel}
                items={options}
                value={currReceiverBufferSizeMsValue + ''}
                onChange={(value) => setSettingsHelper('networkPacketDurationMs', value)}
            />
        </div>
    )
}
